import { type ComponentType, memo } from 'react';
import { type Components } from 'react-markdown';

import { cn } from '@air/utils-tailwind';

import { PrivateWorkspaceAgentPanelMessageContentExporterMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageContentExporterMoniker';
import { PrivateWorkspaceAgentPanelMessagePillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessagePillChip';
import { PrivateWorkspaceAgentPanelMessageShareLinkMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageShareLinkMoniker';
import { PrivateWorkspaceAgentPanelMessageShowcaseMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageShowcaseMoniker';
import { PrivateWorkspaceAgentPanelMessageSkillMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageSkillMoniker';
import { WorkspaceAgentPanelMarkdownLink } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelMarkdownLink';
import { WorkspaceAgentUnknownMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnknownMonikerPill';
import { useWorkspaceAgentMarkdownPills } from '~/components/v2/components/workspace/workspace-agent-panel/markdown/WorkspaceAgentMarkdownPillsProvider';
import { findWorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/findWorkspaceAgentMessagePill';

/**
 * Widens react-markdown's `Components` map to include the custom `<workspace-agent-pill>` element
 * emitted by `remarkWorkspaceAgentPill`. The key is not part of `JSX.IntrinsicElements`, so it
 * cannot live on `Components` directly.
 */
export type WorkspaceAgentMarkdownComponents = Components & {
  'workspace-agent-pill': ComponentType<PrivateWorkspaceAgentMarkdownPillProps>;
  'workspace-agent-share-link-moniker': ComponentType<PrivateWorkspaceAgentMarkdownShareLinkMonikerProps>;
  'workspace-agent-showcase-moniker': ComponentType<PrivateWorkspaceAgentMarkdownShowcaseMonikerProps>;
  'workspace-agent-content-exporter-moniker': ComponentType<PrivateWorkspaceAgentMarkdownContentExporterMonikerProps>;
  'workspace-agent-skill-moniker': ComponentType<PrivateWorkspaceAgentMarkdownSkillMonikerProps>;
  'workspace-agent-unknown-moniker': ComponentType<PrivateWorkspaceAgentMarkdownUnknownMonikerProps>;
};

export type PrivateWorkspaceAgentMarkdownUnknownMonikerProps = {
  monikerId?: string;
  monikerType?: string;
};

const PrivateWorkspaceAgentMarkdownUnknownMoniker = memo(
  ({ monikerId, monikerType }: PrivateWorkspaceAgentMarkdownUnknownMonikerProps) => {
    if (!monikerId || !monikerType) {
      return null;
    }

    return <WorkspaceAgentUnknownMonikerPill monikerId={monikerId} monikerType={monikerType} />;
  },
);

PrivateWorkspaceAgentMarkdownUnknownMoniker.displayName = 'PrivateWorkspaceAgentMarkdownUnknownMoniker';

export type PrivateWorkspaceAgentMarkdownSkillMonikerProps = {
  skillId?: string;
};

const PrivateWorkspaceAgentMarkdownSkillMoniker = memo(
  ({ skillId }: PrivateWorkspaceAgentMarkdownSkillMonikerProps) => {
    if (!skillId) {
      return null;
    }

    return <PrivateWorkspaceAgentPanelMessageSkillMoniker skillId={skillId} />;
  },
);

PrivateWorkspaceAgentMarkdownSkillMoniker.displayName = 'PrivateWorkspaceAgentMarkdownSkillMoniker';

export type PrivateWorkspaceAgentMarkdownShareLinkMonikerProps = {
  shortId?: string;
};

const PrivateWorkspaceAgentMarkdownShareLinkMoniker = memo(
  ({ shortId }: PrivateWorkspaceAgentMarkdownShareLinkMonikerProps) => {
    if (!shortId) {
      return null;
    }

    return <PrivateWorkspaceAgentPanelMessageShareLinkMoniker shortId={shortId} />;
  },
);

PrivateWorkspaceAgentMarkdownShareLinkMoniker.displayName = 'PrivateWorkspaceAgentMarkdownShareLinkMoniker';

export type PrivateWorkspaceAgentMarkdownShowcaseMonikerProps = {
  collectionId?: string;
};

const PrivateWorkspaceAgentMarkdownShowcaseMoniker = memo(
  ({ collectionId }: PrivateWorkspaceAgentMarkdownShowcaseMonikerProps) => {
    if (!collectionId) {
      return null;
    }

    return <PrivateWorkspaceAgentPanelMessageShowcaseMoniker collectionId={collectionId} />;
  },
);

PrivateWorkspaceAgentMarkdownShowcaseMoniker.displayName = 'PrivateWorkspaceAgentMarkdownShowcaseMoniker';

export type PrivateWorkspaceAgentMarkdownContentExporterMonikerProps = {
  taskId?: string;
};

const PrivateWorkspaceAgentMarkdownContentExporterMoniker = memo(
  ({ taskId }: PrivateWorkspaceAgentMarkdownContentExporterMonikerProps) => {
    if (!taskId) {
      return null;
    }

    return <PrivateWorkspaceAgentPanelMessageContentExporterMoniker taskId={taskId} />;
  },
);

PrivateWorkspaceAgentMarkdownContentExporterMoniker.displayName = 'PrivateWorkspaceAgentMarkdownContentExporterMoniker';

export type PrivateWorkspaceAgentMarkdownPillProps = {
  id?: string;
};

const PrivateWorkspaceAgentMarkdownPill = memo(({ id }: PrivateWorkspaceAgentMarkdownPillProps) => {
  const { isStreaming, pills } = useWorkspaceAgentMarkdownPills();
  const pill = findWorkspaceAgentMessagePill(pills, id ?? '');

  if (pill?.type === 'comment' || pill?.type === 'changePreview' || pill?.type === 'table') {
    return (
      <div className="mt-2">
        <PrivateWorkspaceAgentPanelMessagePillChip isStreaming={isStreaming} pill={pill} rawId={id ?? ''} />
      </div>
    );
  }

  return <PrivateWorkspaceAgentPanelMessagePillChip isStreaming={isStreaming} pill={pill} rawId={id ?? ''} />;
});

PrivateWorkspaceAgentMarkdownPill.displayName = 'PrivateWorkspaceAgentMarkdownPill';

/**
 * Must stay a module constant — a new map identity per message remounts the whole markdown subtree.
 */
export const PRIVATE_WORKSPACE_AGENT_MARKDOWN_COMPONENTS: WorkspaceAgentMarkdownComponents = {
  a: ({ children, href }) => (
    <WorkspaceAgentPanelMarkdownLink data-testid="WORKSPACE_AGENT_MESSAGE_LINK" href={href}>
      {children}
    </WorkspaceAgentPanelMarkdownLink>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-gray-6 border-l-2 pl-2 text-gray-11 italic last:mb-0">{children}</blockquote>
  ),
  code: ({ children, className }) => {
    // react-markdown only sets a className on fenced blocks, where it carries the language.
    const isInline = !className;

    return isInline ? (
      <code className="break-all px-1 py-0.5 text-12">{children}</code>
    ) : (
      <code className={cn('block overflow-x-auto p-2 text-12', className)}>{children}</code>
    );
  },
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <h1 className="mb-2 font-semibold text-16 last:mb-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 font-semibold text-14 last:mb-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1 font-semibold text-14 last:mb-0">{children}</h3>,
  h4: ({ children }) => <h4 className="mb-1 font-semibold text-14 last:mb-0">{children}</h4>,
  h5: ({ children }) => <h5 className="mb-1 font-semibold text-14 last:mb-0">{children}</h5>,
  h6: ({ children }) => <h6 className="mb-1 font-semibold text-14 last:mb-0">{children}</h6>,
  hr: () => <hr className="my-3 border-gray-5" />,
  /**
   * An `<img src>` here would have the browser fetch whatever host the agent named, from an
   * authenticated session, before anyone had chosen to look at it — enough on its own to beacon that
   * the message was read. The panel has no use case for an inline image either: assets reach the
   * thread as pills.
   */
  img: ({ alt, src }) => (
    <WorkspaceAgentPanelMarkdownLink data-testid="WORKSPACE_AGENT_MESSAGE_IMAGE_LINK" href={src}>
      {alt || src}
    </WorkspaceAgentPanelMarkdownLink>
  ),
  li: ({ children }) => <li>{children}</li>,
  ol: ({ children }) => <ol className="mb-2 list-outside list-decimal space-y-0.5 pl-4 last:mb-0">{children}</ol>,
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  pre: ({ children }) => <pre className="mb-2 last:mb-0">{children}</pre>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  table: ({ children }) => (
    <div className="my-2 max-w-full overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-left text-12">{children}</table>
    </div>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  td: ({ children }) => <td className="break-words py-2 pr-3 align-top text-gray-12">{children}</td>,
  th: ({ children }) => <th className="py-2 pr-3 align-top font-medium text-gray-11 leading-4">{children}</th>,
  thead: ({ children }) => <thead className="border-gray-3 border-b bg-gray-2">{children}</thead>,
  tr: ({ children }) => <tr className="border-gray-3 border-b last:border-b-0">{children}</tr>,
  ul: ({ children }) => <ul className="mb-2 list-outside list-disc space-y-0.5 pl-4 last:mb-0">{children}</ul>,
  'workspace-agent-pill': PrivateWorkspaceAgentMarkdownPill,
  'workspace-agent-share-link-moniker': PrivateWorkspaceAgentMarkdownShareLinkMoniker,
  'workspace-agent-showcase-moniker': PrivateWorkspaceAgentMarkdownShowcaseMoniker,
  'workspace-agent-content-exporter-moniker': PrivateWorkspaceAgentMarkdownContentExporterMoniker,
  'workspace-agent-skill-moniker': PrivateWorkspaceAgentMarkdownSkillMoniker,
  'workspace-agent-unknown-moniker': PrivateWorkspaceAgentMarkdownUnknownMoniker,
};
